import { RiArrowRightSLine } from '@remixicon/react';
import type { ComponentProps, ReactNode } from 'react';

import { Avatar } from '@playbook/atoms/avatar';
import { cn, cv } from '@playbook/utils';

import css from './styles.module.css';

type ClubSelectorItemEventHandlers = {
	[K in keyof ComponentProps<'button'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'button'>[K];
};

interface ClubSelectorItemDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface ClubSelectorItemProperties
	extends ClubSelectorItemEventHandlers, ClubSelectorItemDataAttributes {
	selected?: boolean;
	traversable?: boolean;

	label: string;
	description?: string;
	brandColor?: string;
	avatarUrl?: string;
	alt?: string;

	trailingIcon?: ReactNode;
	trailingAction?: ReactNode;
}

const DefaultClubSelectorItemTraversable: boolean = false as const;

export function ClubSelectorItem({
	selected = false,
	traversable = DefaultClubSelectorItemTraversable,

	label,
	description,
	brandColor,
	avatarUrl,
	alt = '',

	trailingIcon,
	trailingAction,

	...props
}: ClubSelectorItemProperties): ReactNode {
	let icon: ReactNode = trailingIcon;

	if (traversable) {
		icon = <RiArrowRightSLine />;
	}

	return (
		<div className={cn(css.item, selected && css.selected)}>
			<button
				{...props}
				type="button"
				aria-current={selected ? 'true' : undefined}
				className={css.select}>
				<span
					className={css.badge}
					style={
						brandColor ? cv({ 'color-raw-brand': brandColor }) : undefined
					}>
					<Avatar
						variant={avatarUrl ? 'club' : 'shield'}
						size="md"
						imageUrl={avatarUrl}
						alt={alt}
					/>
				</span>
				<span className={css.text}>
					<span className={css.label}>{label}</span>
					{description && (
						<span className={css.description}>{description}</span>
					)}
				</span>
				{icon && (
					<span aria-hidden className={css.trailingIcon}>
						{icon}
					</span>
				)}
			</button>
			{trailingAction}
		</div>
	);
}
